import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Check, Globe, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { SUPPORTED_LOCALES, STORAGE_KEY } from "@/i18n";

/**
 * Language selector for the header (desktop + mobile).
 * - Compact button with globe + current locale short code (PT/EN/ES)
 * - Dropdown with full native labels (Português / English / Español)
 * - Persists choice to localStorage
 * - Accessible: keyboard nav, aria-expanded, aria-haspopup, aria-controls, Escape to close
 *
 * Props:
 *   variant?: "header" | "header-dark" | "inline"
 */
export const LanguageSwitcher = ({ variant = "header" }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const currentCode = i18n.language;
  const current =
    SUPPORTED_LOCALES.find((l) => l.code === currentCode) || SUPPORTED_LOCALES[0];

  const close = useCallback(() => setOpen(false), []);

  const changeLanguage = useCallback(
    (code) => {
      if (code === i18n.language) {
        close();
        return;
      }
      try {
        window.localStorage.setItem(STORAGE_KEY, code);
      } catch (_) {
        /* ignore */
      }
      i18n.changeLanguage(code);
      close();
      // return focus to the trigger
      setTimeout(() => buttonRef.current?.focus(), 0);
    },
    [i18n, close],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [open, close]);

  // Focus first (or current) item when opening
  useEffect(() => {
    if (open) {
      const idx = SUPPORTED_LOCALES.findIndex((l) => l.code === currentCode);
      setActiveIdx(idx >= 0 ? idx : 0);
      setTimeout(() => {
        listRef.current?.querySelectorAll("[role='menuitemradio']")[idx]?.focus();
      }, 0);
    }
  }, [open, currentCode]);

  const onKeyDownButton = (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onKeyDownItem = (e, idx) => {
    const items = listRef.current?.querySelectorAll("[role='menuitemradio']") || [];
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      buttonRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = (idx + 1) % items.length;
      setActiveIdx(next);
      items[next]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = (idx - 1 + items.length) % items.length;
      setActiveIdx(prev);
      items[prev]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      const last = items.length - 1;
      setActiveIdx(last);
      items[last]?.focus();
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      changeLanguage(SUPPORTED_LOCALES[idx].code);
    } else if (e.key === "Tab") {
      close();
    }
  };

  const btnClasses =
    variant === "header-dark"
      ? "inline-flex items-center gap-1.5 rounded-sm border border-white/25 hover:border-white/60 text-white font-body font-medium text-sm px-2.5 py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      : variant === "inline"
        ? "inline-flex items-center gap-2 rounded-sm border border-[#E5E5E0] hover:border-[#2A3563] text-[#1A1A1A] font-body font-medium text-base px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A3563]/50"
        : "inline-flex items-center gap-1.5 rounded-sm border border-[#E5E5E0] hover:border-[#2A3563] text-[#1A1A1A] font-body font-medium text-sm px-2.5 py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A3563]/50";

  return (
    <div
      ref={wrapperRef}
      className={variant === "inline" ? "w-full" : "relative"}
      data-testid="language-switcher"
    >
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="eliabe-language-menu"
        aria-label={t("language.selectorLabel")}
        data-testid="language-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDownButton}
        className={btnClasses + (variant === "inline" ? " w-full justify-between" : "")}
      >
        <span className="inline-flex items-center gap-1.5">
          <Globe size={16} aria-hidden="true" />
          <span className="tabular-nums">{current.short}</span>
          {variant === "inline" ? (
            <span className="ml-1 text-sm text-[#1A1A1A]/60">— {current.label}</span>
          ) : null}
        </span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={"transition-transform " + (open ? "rotate-180" : "")}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            id="eliabe-language-menu"
            ref={listRef}
            role="menu"
            aria-label={t("language.menu")}
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.14, ease: "easeOut" }}
            data-testid="language-switcher-menu"
            className={
              variant === "inline"
                ? "mt-2 w-full rounded-sm bg-white border border-[#E5E5E0] shadow-md overflow-hidden z-50"
                : "absolute right-0 mt-2 min-w-[180px] rounded-sm bg-white border border-[#E5E5E0] shadow-xl overflow-hidden z-50"
            }
          >
            {SUPPORTED_LOCALES.map((loc, idx) => {
              const isActive = loc.code === currentCode;
              return (
                <li key={loc.code} role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={isActive}
                    tabIndex={idx === activeIdx ? 0 : -1}
                    data-testid={`language-option-${loc.code}`}
                    onClick={() => changeLanguage(loc.code)}
                    onKeyDown={(e) => onKeyDownItem(e, idx)}
                    className={
                      "flex w-full items-center justify-between gap-3 px-4 py-2.5 font-body text-sm text-left transition-colors focus:outline-none focus:bg-[#F0F0EA] hover:bg-[#F0F0EA] " +
                      (isActive
                        ? "text-[#2A3563] font-semibold bg-[#F0F0EA]/60"
                        : "text-[#1A1A1A]")
                    }
                  >
                    <span>{loc.label}</span>
                    {isActive ? (
                      <Check size={16} className="text-[#2A3563]" aria-hidden="true" />
                    ) : (
                      <span aria-hidden="true" className="w-4" />
                    )}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
